import { inject, Injectable, Injector } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, combineLatest, filter, firstValueFrom, map, of, take, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserPreferencesCacheService } from '../shared/services/user-preferences-cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthCustomService {
  private jwtHelper = inject(JwtHelperService);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  // Lazy getter to break the circular DI cycle:
  // AuthCustomService → UserPreferencesCacheService → AuthCustomService
  private get userPreferencesCacheService(): UserPreferencesCacheService {
    return this.injector.get(UserPreferencesCacheService);
  }
  private loadingTokenPromise: Promise<string | null> | null = null;
  private readonly tokenStorageKey = 'hub.auth.token';
  private readonly authResolveTimeoutMs = 10000;
  private readonly silentTokenTimeoutMs = 8000;

  token: string = '';
  jwtPayload!: any;
  userEmail!: string;

  isLoggedIn() {
    return this.hasValidToken();
  }

  private setToken(token: string | null) {
    this.token = token || '';
    if (!this.token) {
      this.jwtPayload = null;
      this.userEmail = '';
      return;
    }

    this.jwtPayload = this.jwtHelper.decodeToken(this.token);
    this.userEmail = this.jwtPayload?.email || '';
  }

  private isInteractionRequiredError(error: any): boolean {
    const code = error?.error || error?.code;
    return code === 'login_required' || code === 'consent_required' || code === 'interaction_required';
  }

  private clearAuth0StorageKeys() {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('@@auth0spajs@@') || key.startsWith('a0.spajs.'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  async isAuthenticatedResolved(): Promise<boolean> {
    return firstValueFrom(
      combineLatest([this.authService.isLoading$, this.authService.isAuthenticated$]).pipe(
        filter(([isLoading]) => !isLoading),
        map(([, isAuthenticated]) => isAuthenticated),
        take(1),
        timeout({ first: this.authResolveTimeoutMs }),
        catchError(() => of(false))
      )
    );
  }

  private async getSilentTokenWithTimeout(): Promise<string | null> {
    const tokenPromise = firstValueFrom(this.authService.getAccessTokenSilently());
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), this.silentTokenTimeoutMs);
    });

    const token = await Promise.race<string | null>([tokenPromise, timeoutPromise]);
    return token;
  }

  async ensureToken(): Promise<string | null> {
    if (this.token && !this.jwtHelper.isTokenExpired(this.token)) {
      return this.token;
    }

    const namespacedToken = localStorage.getItem(this.tokenStorageKey);
    if (namespacedToken && namespacedToken !== '' && !this.jwtHelper.isTokenExpired(namespacedToken)) {
      const auth0User = await firstValueFrom(this.authService.user$);
      const auth0Email = (auth0User?.email || '').toLowerCase();
      const tokenEmail = (this.jwtHelper.decodeToken(namespacedToken)?.email || '').toLowerCase();
      if (auth0Email && tokenEmail && auth0Email !== tokenEmail) {
        localStorage.removeItem(this.tokenStorageKey);
      } else {
        this.setToken(namespacedToken);
        return this.token;
      }
    }

    if (this.loadingTokenPromise) {
      return this.loadingTokenPromise;
    }

    const isAuthenticated = await this.isAuthenticatedResolved();
    if (!isAuthenticated) {
      this.setToken(null);
      return null;
    }

    this.loadingTokenPromise = (async () => {
      try {
        const newToken = await this.getSilentTokenWithTimeout();
        if (!newToken) {
          this.setToken(null);
          return null;
        }
        this.userPreferencesCacheService.clearUserCache();
        localStorage.setItem(this.tokenStorageKey, newToken);
        this.setToken(newToken);
        return newToken;
      } catch (error) {
        if (this.isInteractionRequiredError(error)) {
          localStorage.removeItem(this.tokenStorageKey);
          this.setToken(null);
          return null;
        }
        throw error;
      } finally {
        this.loadingTokenPromise = null;
      }
    })();

    return this.loadingTokenPromise;
  }

  getToken(): string {
    if (!this.token) {
      const tokenLS = localStorage.getItem(this.tokenStorageKey);
      if (tokenLS && tokenLS !== '' && !this.jwtHelper.isTokenExpired(tokenLS)) {
        this.setToken(tokenLS);
      }
    }
    return this.token;
  }

  logout() {
    localStorage.removeItem(this.tokenStorageKey);
    this.clearAuth0StorageKeys();
    this.setToken(null);
    this.authService.logout({
      logoutParams: {
        returnTo: environment.url + 'welcome', // Redireciona para a página de boas-vindas após o logout
      },
    });

  }

  login() {
    this.authService.loginWithRedirect({
      appState: {
        target: '/my-account',
      },
      authorizationParams: {
        prompt: 'login',
      },
    });
  }

  somePermission(roles: string[]) {
    for (const role of roles) {
      if (this.hasPermission(role)) {
        return true;
      }
    }
    return false;
  }

  isAdmin() {
    return this.hasPermission('ADMIN');
  }

  isSupport() {
    return this.hasPermission('SUPPORT');
  }

  hasPermission(role: string) {
    if (this.jwtPayload && this.jwtPayload.permissions) {
      return this.jwtPayload.permissions.includes(role);
    } else {
      return false;
    }
  }

  hasValidToken() {
    const currentToken = this.getToken();
    return !!currentToken && !this.jwtHelper.isTokenExpired(currentToken);
  }
}
